import {
  Spacer,
  PageDS,
  TabMenu,
  AddOutlined,
  triggerToast,
  ToastContainerDS,
} from "@qivia/ui";
import { useTranslation } from "react-i18next";
import { useEffect, useMemo, useState } from "react";
import { AccountTab } from "./accountTab";
import { useNavigate, useParams } from "react-router-dom";
import { UserManagementTab } from "./userManagementTab";
import { useAppDispatch, useAppSelector } from "../../../redux/hooks";
import {
  selectManagerCreationStatus,
  selectManagersList,
  selectUpdateUserRoleStatus,
  UsersSlice,
} from "./usersSlice";
import { selectManager } from "../homeSlice";
import "../../../i18n/i18n";
import { supportPhonesByCompanyGroupUuid } from "../../../constants/support";

export const Users = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const params = useParams();
  const dispatch = useAppDispatch();
  const userCreationStatus = useAppSelector(selectManagerCreationStatus);
  const updateUserRoleStatus = useAppSelector(selectUpdateUserRoleStatus);
  const manager = useAppSelector(selectManager);
  const managers = useAppSelector(selectManagersList);

  const otherSupportPhones = useMemo(
    () =>
      managers && managers.length > 0 && managers[0].companyGroup
        ? supportPhonesByCompanyGroupUuid[managers[0].companyGroup]
        : undefined,
    [managers],
  );

  const [selectedTab, setSelectedTab] = useState<string>(
    params.tab ?? "account",
  );

  const tabs = [
    {
      name: "account",
      title: t("users.tab.account"),
    },
    {
      name: "management",
      title: t("users.tab.management"),
    },
  ];

  const isOwnerManager = useMemo(
    () => manager && manager.role === "OWNER",
    [manager],
  );

  useEffect(() => {
    if (selectedTab) {
      navigate(
        `/home/users/${selectedTab}${
          params.id !== undefined ? `/${params.id}` : ""
        }`,
      );
    }
  }, [navigate, params.id, selectedTab]);

  useEffect(() => {
    if (userCreationStatus === "success") {
      triggerToast(t("users.modal.success") || "", "valid");
      void dispatch(UsersSlice.actions.resetUserCreationStatus());
    } else if (userCreationStatus === "failed") {
      triggerToast(t("users.modal.failure") || "", "error");
      void dispatch(UsersSlice.actions.resetUserCreationStatus());
    }
  }, [userCreationStatus, t, dispatch]);

  useEffect(() => {
    if (updateUserRoleStatus === "success") {
      triggerToast(t("users.management.updateRole.success") || "", "valid");
      void dispatch(UsersSlice.actions.resetUpdateUserRoleStatus());
    } else if (updateUserRoleStatus === "failed") {
      triggerToast(t("users.management.updateRole.failure") || "", "error");
      void dispatch(UsersSlice.actions.resetUpdateUserRoleStatus());
    }
  }, [dispatch, updateUserRoleStatus, t]);

  return (
    <PageDS
      title={t(`users.${selectedTab}`)}
      toaster={<ToastContainerDS />}
      cta={
        selectedTab === "management" && isOwnerManager
          ? {
              leftIcon: <AddOutlined />,
              title: t(`users.tab.management.button.add`),
              action: () => navigate("/home/user_creation"),
            }
          : undefined
      }
      topElement={
        <>
          <TabMenu
            tabs={tabs}
            selectedTab={selectedTab}
            setSelectedTab={setSelectedTab}
          />
          <Spacer y={0.5} />
        </>
      }
      supportButton={{
        showMail: !otherSupportPhones,
        otherPhones: otherSupportPhones,
      }}
    >
      <AccountTab />
      <UserManagementTab />
    </PageDS>
  );
};
